import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import unleashWordmark from '@/icons/unleash-wordmark-light.png';

import { IUser } from '@/types/user';

import { getInitials } from '@/utils/stringUtils';

import { ClaimSubmissionModal } from './ClaimSubmissionModal';
import { MenuNav } from './MenuNav';

interface HeaderProps {
  user: IUser;
  onSignOut: () => void;
}

function Header({ user, onSignOut }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const menuDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      if (isMobile) {
        if (
          mobileMenuRef.current &&
          !mobileMenuRef.current.contains(target) &&
          menuRef.current &&
          !menuRef.current.contains(target)
        ) {
          setIsMenuOpen(false);
        }
      } else {
        if (menuRef.current && !menuRef.current.contains(target)) {
          setIsMenuOpen(false);
        }
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMenuOpen, isMobile]);

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuItemClick = (action?: () => void) => {
    setIsMenuOpen(false);
    if (action) {
      action();
    }
  };

  const hasPicture = user.picture && !imageError;

  return (
    <>
      <header className="sticky top-0 z-40 h-14 border-b border-gray-200 bg-white/90 shadow-xs backdrop-blur-xs">
        <div className="mx-auto flex size-full max-w-screen-2xl items-center justify-between px-4 md:px-6">
          <Link
            to="/dashboard"
            className="flex min-w-0 shrink-0 items-center transition-opacity hover:opacity-80"
          >
            <img
              src={unleashWordmark}
              alt="Unleash"
              className="h-8 w-auto object-contain object-left md:h-9"
            />
          </Link>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowClaimModal(true)}
              className="hidden h-9 items-center rounded-lg border border-red-500 px-4 text-sm font-medium text-red-500 transition-colors duration-200 hover:bg-red-50 md:inline-flex"
            >
              + New Claim
            </button>
            <div className="relative" ref={menuRef}>
              <button
                onClick={handleMenuToggle}
                className="flex items-center rounded-full transition-transform hover:scale-105 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-hidden"
                aria-label="User menu"
                aria-expanded={isMenuOpen}
              >
                {hasPicture ? (
                  <img
                    src={user.picture}
                    alt="User profile"
                    className={`rounded-full border-2 border-gray-300 object-cover transition-colors hover:border-blue-500 ${
                      isMobile ? 'h-8 w-8' : 'h-10 w-10'
                    }`}
                    referrerPolicy="no-referrer"
                    crossOrigin="anonymous"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div
                    className={`flex items-center justify-center rounded-full border-2 border-gray-300 bg-blue-500 font-semibold text-white transition-colors hover:border-blue-500 ${
                      isMobile ? 'h-8 w-8 text-xs' : 'h-10 w-10 text-sm'
                    }`}
                  >
                    {getInitials(user.name)}
                  </div>
                )}
              </button>

              {isMenuOpen && !isMobile && (
                <div
                  ref={menuDropdownRef}
                  data-testid="user-menu-dropdown"
                  className="absolute right-0 z-50 mt-2 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg transition-all duration-200 ease-in-out"
                >
                  <div className="border-b border-gray-100 px-4 py-3">
                    <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                    <p className="truncate text-xs text-gray-500">{user.email}</p>
                  </div>

                  <MenuNav
                    handleMenuItemClick={handleMenuItemClick}
                    onSignOut={onSignOut}
                    variant="desktop"
                    user={user}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      {/* Mobile Menu Overlay */}
      {isMenuOpen && isMobile && (
        <button
          type="button"
          className="fixed inset-0 z-40 cursor-default border-0 bg-black/20 p-0"
          aria-label="Close menu"
          onClick={() => setIsMenuOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setIsMenuOpen(false);
          }}
        />
      )}

      {/* Mobile Menu - Slide in from right */}
      <div
        ref={mobileMenuRef}
        className={`fixed top-0 right-0 z-50 h-full w-72 transform bg-white shadow-lg transition-transform duration-300 ease-in-out md:hidden ${
          isMenuOpen && isMobile ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="border-b border-gray-100 p-4">
          <p className="text-sm font-semibold text-gray-900">{user.name}</p>
          <p className="truncate text-xs text-gray-500">{user.email}</p>
        </div>
        <MenuNav
          handleMenuItemClick={handleMenuItemClick}
          onSignOut={onSignOut}
          variant="mobile"
          user={user}
        />
      </div>

      {showClaimModal && <ClaimSubmissionModal onClose={() => setShowClaimModal(false)} />}
    </>
  );
}

export { Header };
